import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
  DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Handshake, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

const CATEGORIES = [
  ["design_subcontractor", "Design Engineering Subcontractor"],
  ["specialist",           "Specialist Consultant"],
  ["supplier",             "Supplier (Goods / Equipment)"],
  ["rental",               "Rented Asset / Equipment Hire"],
  ["calibration",          "Calibration / Lab Services"],
  ["site_service",         "Site Service / Inspection Agency"],
  ["other",                "Other"],
];

const STREAMS = [
  ["C", "Stream C · Sub-consultants (recommended for design / specialist)"],
  ["B", "Stream B · Outbound Goods & Equipment"],
  ["D", "Stream D · JV / Partnership"],
];

export default function RaiseProcurementDialog({ project, deliverable, onRaised, trigger }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    stream: "C",
    procurement_category: "design_subcontractor",
    scope_summary: deliverable?.title || "",
    estimated_value: 0,
    currency: "USD",
    requesting_dept: "Engineering",
    notes: "",
  });
  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(
        `/projects/${project.id}/deliverables/${deliverable.id}/raise-procurement`,
        form,
      );
      toast.success(`Procurement raised — ${data.ref}`);
      onRaised?.(data);
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const alreadyRaised = !!deliverable?.procurement_request_id;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Handshake size={18} /> Raise procurement request
          </DialogTitle>
          <DialogDescription>
            Pre-filled with the project + deliverable + work-package context.
            The request opens at <strong>P1 · Identify</strong> in Procurement
            with this project already linked. The awarded vendor will write
            back into this deliverable automatically when Procurement reaches
            P6 · Award.
          </DialogDescription>
        </DialogHeader>

        {alreadyRaised ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            ⚠️ A procurement request was already raised for this deliverable —
            <strong className="ml-1 font-mono-alt">{deliverable.procurement_ref}</strong>.
            Open it from the Procurement module to advance the lifecycle.
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
              <div className="flex items-start gap-2">
                <Sparkle size={14} className="mt-0.5 flex-shrink-0" />
                <div className="space-y-0.5">
                  <div><strong>Project:</strong> {project.name}</div>
                  <div><strong>Deliverable:</strong> {deliverable.title}</div>
                  {deliverable.contract_clause && (
                    <div><strong>Work package / clause:</strong> {deliverable.contract_clause}</div>
                  )}
                  {project.current_phase && (
                    <div><strong>Project phase:</strong> {project.current_phase}</div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <Label>Procurement stream *</Label>
              <Select value={form.stream} onValueChange={(v) => setField("stream", v)}>
                <SelectTrigger data-testid="proc-stream"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STREAMS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Category *</Label>
              <Select value={form.procurement_category}
                       onValueChange={(v) => setField("procurement_category", v)}>
                <SelectTrigger data-testid="proc-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Scope summary *</Label>
              <Textarea required rows={3} value={form.scope_summary}
                onChange={(e) => setField("scope_summary", e.target.value)}
                placeholder="Brief technical description (SOW snippet) — Procurement will use this to launch the sourcing process."
                data-testid="proc-scope" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Estimated value</Label>
                <Input type="number" value={form.estimated_value}
                  onChange={(e) => setField("estimated_value", Number(e.target.value))}
                  data-testid="proc-value" />
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={form.currency} onValueChange={(v) => setField("currency", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="AED">AED</SelectItem>
                    <SelectItem value="SOS">SOS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Requesting department</Label>
              <Input value={form.requesting_dept}
                     onChange={(e) => setField("requesting_dept", e.target.value)} />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={2} value={form.notes}
                onChange={(e) => setField("notes", e.target.value)}
                placeholder="Anything Procurement should know — urgency, prequalified vendor list, BPA call-off…" />
            </div>

            <DialogFooter>
              <Button type="submit" disabled={busy} className="btn-primary-blue"
                      data-testid="submit-raise-procurement">
                {busy ? "Raising…" : "Raise procurement request"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
